import { useMutation, useQueryClient } from '@tanstack/react-query';
import { jobPositionService } from '../services/jobPosition.service';
import type {
  UpdateApplicationRequest,
  JobPositionCandidateWithDetails,
} from '../types/jobPositionCandidate.types';
import { JobPositionId, CandidateId } from '@/shared/types/branded.types';

interface MutationParams {
  positionId: string;
  candidateId: string;
  data: UpdateApplicationRequest;
}

export function useUpdateApplication(): {
  updateApplication: (
    params: MutationParams,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => void;
  updateApplicationAsync: (params: MutationParams) => Promise<JobPositionCandidateWithDetails>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ positionId, candidateId, data }: MutationParams) =>
      jobPositionService.updateCandidate(
        { positionId: JobPositionId(positionId), candidateId: CandidateId(candidateId) },
        data
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['job-position-candidates', variables.positionId],
      });
    },
  });

  return {
    updateApplication: mutation.mutate as (
      params: MutationParams,
      options?: { onSuccess?: () => void; onError?: (error: Error) => void }
    ) => void,
    updateApplicationAsync: mutation.mutateAsync as (
      params: MutationParams
    ) => Promise<JobPositionCandidateWithDetails>,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
}
